import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  OutlinedInput,
} from "@mui/material";
import SoftButton from "components/SoftButton";

const CustomizedConcrete = ({ onAreaChange, onClose, data, initialValues }) => {
  // `data` hentes asynkront av CalculateArea og er null både før kallet er
  // ferdig og hvis det feilet. Å destrukturere den rett ut kastet
  // «Cannot destructure property 'concrete_quality' of 'data' as it is null»
  // og hvitskjermet hele siden — en nede backend skal ikke ta ned frontenden.
  const {
    concrete_quality = [],
    concrete_stone = [],
    concrete_reduction = [],
    concrete_consistency = [],
  } = data || {};

  // Uten valglistene er skjemaet et skall man ikke kan fylle ut. Da sier vi det,
  // i stedet for å vise fire tomme nedtrekk som ser ødelagte ut.
  const optionsMissing =
    !concrete_quality.length ||
    !concrete_stone.length ||
    !concrete_reduction.length ||
    !concrete_consistency.length;

  const [concreteType, setConcreteType] = useState("");
  const [quality, setQuality] = useState("");
  const [stoneType, setStoneType] = useState("");
  const [stoneReduction, setStoneReduction] = useState("");
  const [consistency, setConsistency] = useState("");


  const [additionalChoices, setAdditionalChoices] = useState({
    lowCarbon: false,
    soilMoist: false,
    frostproof: false,
    livestockFarming: false,
  });

  const [errors, setErrors] = useState({});

  // prefill from initialValues (coming from delivery.customConcretes[0])
  useEffect(() => {
    if (!initialValues) return;

    setConcreteType(initialValues.name || "");
    setQuality((concrete_quality.find((x) => String(x.id) === String(initialValues.quality)) || {}).name || "");
    setStoneType((concrete_stone.find((x) => String(x.id) === String(initialValues.stone)) || {}).name || "");
    setStoneReduction(
      (concrete_reduction.find((x) => String(x.id) === String(initialValues.reduction)) || {}).name || ""
    );
    setConsistency(
      (concrete_consistency.find((x) => String(x.id) === String(initialValues.consistency)) || {}).name || ""
    );
    setAdditionalChoices({
      lowCarbon: !!initialValues.lowCarbon,
      soilMoist: !!initialValues.curbstone,
      frostproof: !!(initialValues.frostProof ?? initialValues.frostproof),
      livestockFarming: !!initialValues.farming,
    });
  }, [initialValues, concrete_quality, concrete_stone, concrete_reduction, concrete_consistency]);

  const validateForm = () => {
    const temp = {};
    if (!concreteType) temp.concreteType = "Betongtype er påkrevd";
    if (!quality) temp.quality = "Kvalitet er påkrevd";
    if (!stoneType) temp.stoneType = "Steintype er påkrevd";
    if (!stoneReduction) temp.stoneReduction = "Steinreduksjon er påkrevd";
    if (!consistency) temp.consistency = "Konsistens er påkrevd";
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleAdditionalChoiceChange = (ev) => {
    setAdditionalChoices((p) => ({ ...p, [ev.target.name]: ev.target.checked }));
  };

  // save -> return single object in array like before
  const handleSaveClick = () => {
    if (!validateForm()) return;

    const qualityId = concrete_quality.find((q) => q.name === quality)?.id || 0;
    const stoneId = concrete_stone.find((s) => s.name === stoneType)?.id || 0;
    const reductionId = concrete_reduction.find((r) => r.name === stoneReduction)?.id || 0;
    const consistencyId = concrete_consistency.find((c) => c.name === consistency)?.id || 0;

    const selectedValues = [
      {
        id: String(initialValues?.id ?? 12),
        name: concreteType,
        quality: String(qualityId),
        stone: String(stoneId),
        consistency: String(consistencyId),
        reduction: String(reductionId),
        lowCarbon: !!additionalChoices.lowCarbon,
        curbstone: !!additionalChoices.soilMoist,
        frostProof: !!additionalChoices.frostproof,
        farming: !!additionalChoices.livestockFarming,
        is_custom: 1,
      },
    ];

    onAreaChange(selectedValues);
    onClose();
  };

  if (optionsMissing) {
    return (
      <div style={{ padding: "28px 24px", maxWidth: 420 }}>
        <Typography variant="h6" sx={{ color: "#000", fontWeight: "bold", mb: 1 }}>
          Betongvalgene kunne ikke lastes
        </Typography>
        <Typography variant="body2" sx={{ color: "#67748e", mb: 3 }}>
          Kvalitet, steintype, reduksjon og konsistens hentes fra serveren, og
          svaret kom ikke. Lukk og prøv igjen — vedvarer det, er det serveren og
          ikke nettleseren.
        </Typography>
        <SoftButton variant="gradient" color="info" onClick={onClose}>
          Lukk
        </SoftButton>
      </div>
    );
  }

  return (
    <div
      style={{
        overflowY: "scroll",
        maxHeight: "100vh",
        padding: "16px",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      <style>{`div::-webkit-scrollbar{display:none}`}</style>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h2" sx={{ display: "flex", justifyContent: "center", color: "#000", mt: 2 }}>
            <span style={{ color: "#006bb7" }}>
              Tilpasset <span style={{ color: "#000" }}>betong</span>!
            </span>
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h5" sx={{ color: "#000", fontWeight: "bold" }}>
            Betongtype :
          </Typography>
          <TextField
            variant="outlined"
            placeholder="Tilpasset betong"
            fullWidth
            sx={{ mt: 1 }}
            value={concreteType}
            onChange={(e) => setConcreteType(e.target.value)}
            error={!!errors.concreteType}
            helperText={errors.concreteType}
            InputProps={{
              sx: {
                padding: "10px 14px !important",
                "& input": { width: "calc(100% - 0px) !important" },
              },
            }}
          />
        </Grid>

        {/* Quality */}
        <Grid item xs={12} sm={6}>
          <Typography variant="h6" sx={{ color: "#000", fontWeight: "bold" }}>
            KVALITET :
          </Typography>
          <FormControl sx={{ width: "100%" }}>
            <Select
              displayEmpty
              value={quality}
              onChange={(e) => setQuality(e.target.value)}
              input={<OutlinedInput />}
              sx={{ height: "40px !important", cursor: "pointer" }}
            >
              <MenuItem disabled value="">
                <Typography variant="body1" style={{ fontSize: "14px" }}>
                  <em>Velg Kvalitet </em>
                </Typography>
              </MenuItem>
              {concrete_quality.map((q) => (
                <MenuItem key={q.id} value={q.name}>
                  {q.name}
                </MenuItem>
              ))}
            </Select>
            {!!errors.quality && <FormHelperText sx={{ color: "error.main" }}>{errors.quality}</FormHelperText>}
          </FormControl>
        </Grid>

        {/* Stone */}
        <Grid item xs={12} sm={6}>
          <Typography variant="h6" sx={{ color: "#000", fontWeight: "bold" }}>
            STEINTYPE :
          </Typography>
          <FormControl sx={{ width: "100%" }}>
            <Select
              displayEmpty
              value={stoneType}
              onChange={(e) => setStoneType(e.target.value)}
              input={<OutlinedInput />}
              sx={{ height: "40px !important", cursor: "pointer" }}
            >
              <MenuItem disabled value="">
                <Typography variant="body1" style={{ fontSize: "14px" }}>
                  <em>Velg Steintype </em>
                </Typography>
              </MenuItem>
              {concrete_stone.map((s) => (
                <MenuItem key={s.id} value={s.name}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
            {!!errors.stoneType && (
              <FormHelperText sx={{ color: "error.main" }}>{errors.stoneType}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Reduction */}
        <Grid item xs={12} sm={6}>
          <Typography variant="h6" sx={{ color: "#000", fontWeight: "bold" }}>
            STEINREDUKSJON :
          </Typography>
          <FormControl sx={{ width: "100%" }}>
            <Select
              displayEmpty
              value={stoneReduction}
              onChange={(e) => setStoneReduction(e.target.value)}
              input={<OutlinedInput />}
              sx={{ height: "40px !important", cursor: "pointer" }}
            >
              <MenuItem disabled value="">
                <Typography variant="body1" style={{ fontSize: "14px" }}>
                  <em>Velg Reduksjon </em>
                </Typography>
              </MenuItem>
              {concrete_reduction.map((r) => (
                <MenuItem key={r.id} value={r.name}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
            {!!errors.stoneReduction && (
              <FormHelperText sx={{ color: "error.main" }}>{errors.stoneReduction}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Consistency */}
        <Grid item xs={12} sm={6}>
          <Typography variant="h6" sx={{ color: "#000", fontWeight: "bold" }}>
            KONSISTENS :
          </Typography>
          <FormControl sx={{ width: "100%" }}>
            <Select
              displayEmpty
              value={consistency}
              onChange={(e) => setConsistency(e.target.value)}
              input={<OutlinedInput />}
              sx={{ height: "40px !important", cursor: "pointer" }}
            >
              <MenuItem disabled value="">
                <Typography variant="body1" style={{ fontSize: "14px" }}>
                  <em>Velg Konsistens </em>
                </Typography>
              </MenuItem>
              {concrete_consistency.map((c) => (
                <MenuItem key={c.id} value={c.name}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
            {!!errors.consistency && (
              <FormHelperText sx={{ color: "error.main" }}>{errors.consistency}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Extra choices */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ color: "#000", fontWeight: "bold" }}>
            Tilleggsvalg:
          </Typography>
          <Grid container mx={2}>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={additionalChoices.lowCarbon}
                    onChange={handleAdditionalChoiceChange}
                    name="lowCarbon"
                  />
                }
                label="Lavkarbon"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={additionalChoices.soilMoist}
                    onChange={handleAdditionalChoiceChange}
                    name="soilMoist"
                  />
                }
                label="Jordfuktighet"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={additionalChoices.frostproof}
                    onChange={handleAdditionalChoiceChange}
                    name="frostproof"
                  />
                }
                label="Frostsikker"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={additionalChoices.livestockFarming}
                    onChange={handleAdditionalChoiceChange}
                    name="livestockFarming"
                  />
                }
                label="Husdyrbruk"
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={2} mt={2} mx={1}>
          <Grid item xs={6}>
            <SoftButton
              variant="outlined"
              color="info"
              fullWidth
              sx={{ height: "45px", borderRadius: "7px", display: "flex", justifyContent: "center", alignItems: "center" }}
              onClick={onClose}
            >
              LUKK
            </SoftButton>
          </Grid>
          <Grid item xs={6}>
            <SoftButton
              variant="contained"
              color="info"
              fullWidth
              sx={{ height: "45px", borderRadius: "7px", display: "flex", justifyContent: "center", alignItems: "center" }}
              onClick={handleSaveClick}
            >
              LAGRE
            </SoftButton>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

CustomizedConcrete.propTypes = {
  onAreaChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  // Ikke `isRequired`: forelderen sender med rette null mens kallet pågår og
  // hvis det feilet. Komponenten håndterer begge.
  data: PropTypes.object,
  initialValues: PropTypes.object,
};

CustomizedConcrete.defaultProps = {
  data: null,
  initialValues: null,
};

export default CustomizedConcrete;
